







































































































































































import { Component, Vue } from 'vue-property-decorator';
import { Route } from 'vue-router';
import { AlbumUploadService } from '@/api';
import { APIError } from '@/api/api';
import { albumUpload } from '@/store/modules/albumUpload';
import { AlbumUploadFormTrack } from '@/types/albumUpload';
import AlbumUploadTrackRow from '@/components/album-upload/AlbumUploadTrackRow.vue';
import UBlock from '@/ui-lib/block/Block.vue';
import UButton from '@/ui-lib/button/Button.vue';
import UTextField from '@/ui-lib/text-field/TextField.vue';
import UAlert from '@/ui-lib/alert/Alert.vue';

const MAX_TRACK_SIZE = 100 * 1024 * 1024;
const MAX_COVER_SIZE = 5 * 1024 * 1024;
const MAX_TRACKS = 100;
const ISRC_LENGTH = 12;
const MIN_YEAR = 1500;

function isAbortError(e: unknown): boolean {
  return (e as Error)?.name === 'AbortError';
}

// Бизнес-ошибки бэка (битый файл, неверная обложка) приходят в validationFails
// с общим message «Проверьте данные» — показываем конкретику из validationFails.
function apiErrorText(e: unknown, fallback: string): string {
  const err = e as APIError;
  const fails = err?.validationFails;

  if (fails) {
    const messages = Object.keys(fails).reduce<string[]>((acc, key) => acc.concat(fails[key]), []);
    if (messages.length) {
      return messages.join(' ');
    }
  }

  return err?.message || fallback;
}

// В class-style компонентах in-component навигационные гарды не работают без
// явной регистрации.
Component.registerHooks(['beforeRouteLeave']);

@Component<AlbumUploadFormView>({
  components: {
    AlbumUploadTrackRow,
    UAlert,
    UBlock,
    UButton,
    UTextField
  },

  metaInfo() {
    return {
      title: 'Добавление альбома'
    };
  }
})
export default class AlbumUploadFormView extends Vue {
  public draftId: number | null = null;
  private draftPromise: Promise<number> | null = null;
  private nextLocalId = 1;
  private aborts: Record<number, AbortController> = {};

  public albumName = '';
  public artistName = '';
  public year = '';

  public albumCoverPreview = '';
  public coverUploading = false;

  public tracks: AlbumUploadFormTrack[] = [];

  public isCommitting = false;
  public errorMessage = '';
  public fails: Record<string, string[]> = {};

  public playingLocalId: number | null = null;
  private audio: HTMLAudioElement | null = null;
  private playingUrl = '';

  private committed = false;

  mounted() {
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    // Прерываем незавершённые заливки, чтобы не лить файлы в брошенный черновик.
    Object.values(this.aborts).forEach(controller => controller.abort());
    this.stopPlayback();
    if (this.albumCoverPreview) {
      URL.revokeObjectURL(this.albumCoverPreview);
      this.albumCoverPreview = '';
    }
  }

  // Предупреждаем о потере данных при закрытии/перезагрузке вкладки.
  private onBeforeUnload = (e: BeforeUnloadEvent): void => {
    if (this.hasContent) {
      e.preventDefault();
      e.returnValue = '';
    }
  };

  // И при уходе на другой роут внутри приложения.
  beforeRouteLeave(to: Route, from: Route, next: (proceed?: boolean) => void) {
    if (this.committed || !this.hasContent) {
      next();
      return;
    }
    if (window.confirm('Загрузка не отправлена. Файлы и заполненные поля будут потеряны. Уйти со страницы?')) {
      next();
    } else {
      next(false);
    }
  }

  get hasContent(): boolean {
    return (
      this.tracks.length > 0 ||
      !!this.albumName ||
      !!this.artistName ||
      !!this.year ||
      !!this.albumCoverPreview
    );
  }

  get canSubmit(): boolean {
    return (
      this.tracks.length > 0 &&
      this.tracks.every(t => t.status === 'uploaded' && !t.deleting) &&
      !this.coverUploading
    );
  }

  private async ensureDraft(): Promise<number> {
    if (this.draftId !== null) {
      return this.draftId;
    }

    if (!this.draftPromise) {
      this.draftPromise = (async () => {
        try {
          const res = await AlbumUploadService.createDraft();
          this.draftId = res.id;
          return res.id;
        } catch (e) {
          // Разрешаем повторную попытку при следующей заливке.
          this.draftPromise = null;
          throw e;
        }
      })();
    }

    return this.draftPromise;
  }

  public async coverChangeHandler(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file) {
      return;
    }

    const nextFails = { ...this.fails };
    delete nextFails.albumCover;
    this.fails = nextFails;

    if (file.size > MAX_COVER_SIZE) {
      this.fails = { ...this.fails, albumCover: ['Обложка больше 5 мб.'] };
      return;
    }

    this.coverUploading = true;

    try {
      const id = await this.ensureDraft();
      await AlbumUploadService.uploadCover(id, file);
      if (this.albumCoverPreview) {
        URL.revokeObjectURL(this.albumCoverPreview);
      }
      this.albumCoverPreview = URL.createObjectURL(file);
    } catch (e) {
      this.fails = {
        ...this.fails,
        albumCover: [apiErrorText(e, 'Не удалось загрузить обложку.')]
      };
    }

    this.coverUploading = false;
  }

  public async tracksChangeHandler(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';

    if (files.length === 0) {
      return;
    }

    this.errorMessage = '';
    const errors: string[] = [];

    for (const file of files) {
      if (this.tracks.length >= MAX_TRACKS) {
        errors.push(`Добавлено максимум ${MAX_TRACKS} файлов, остальные пропущены.`);
        break;
      }

      if (file.size > MAX_TRACK_SIZE) {
        errors.push(`${file.name}: файл больше 100 мб.`);
        continue;
      }

      this.addTrack(file);
    }

    if (errors.length) {
      this.errorMessage = errors.join(' ');
    }
  }

  private addTrack(file: File) {
    const track: AlbumUploadFormTrack = {
      localId: this.nextLocalId++,
      file,
      trackUploadId: null,
      originalFilename: file.name,
      title: '',
      isrc: '',
      duration: null,
      status: 'uploading',
      error: '',
      deleting: false
    };
    this.tracks.push(track);

    this.uploadIntoTrack(track, file);
  }

  public retryTrack(track: AlbumUploadFormTrack) {
    this.uploadIntoTrack(track, track.file);
  }

  private async uploadIntoTrack(track: AlbumUploadFormTrack, file: File) {
    track.status = 'uploading';
    track.error = '';

    const controller = new AbortController();
    this.aborts[track.localId] = controller;

    try {
      const id = await this.ensureDraft();
      const meta = await AlbumUploadService.uploadTrackFile(id, file, {
        signal: controller.signal
      });

      // Строку могли отменить/удалить, пока файл грузился. Тогда удаляем
      // залитый файл на сервере, чтобы он не повис и не сломал commit.
      if (!this.tracks.some(t => t.localId === track.localId)) {
        if (this.draftId !== null) {
          AlbumUploadService.deleteTrackFile(this.draftId, meta.id).catch(() => undefined);
        }
        return;
      }

      track.trackUploadId = meta.id;
      track.originalFilename = meta.originalFilename || file.name;
      track.title = meta.title || '';
      track.isrc = meta.isrc || '';
      track.duration = meta.duration;
      track.status = 'uploaded';

      this.prefillAlbumFields(meta.artist, meta.album, meta.year);
    } catch (e) {
      if (isAbortError(e)) {
        return;
      }
      track.status = 'error';
      track.error = apiErrorText(e, 'Не удалось загрузить файл.');
    } finally {
      delete this.aborts[track.localId];
    }
  }

  // Артиста/альбом/год подставляем из тегов только если поле ещё пустое —
  // первый трек заполняет, последующие не перетирают введённое.
  private prefillAlbumFields(artist: string | null, album: string | null, year: string | null) {
    if (!this.artistName && artist) {
      this.artistName = artist;
    }
    if (!this.albumName && album) {
      this.albumName = album;
    }
    if (!this.year && year) {
      this.year = year;
    }
  }

  public onTrackUpdate(track: AlbumUploadFormTrack, patch: { title?: string; isrc?: string }) {
    if (patch.title !== undefined) {
      track.title = patch.title;
    }
    if (patch.isrc !== undefined) {
      track.isrc = patch.isrc;
    }
  }

  public async removeTrack(track: AlbumUploadFormTrack) {
    if (this.playingLocalId === track.localId) {
      this.stopPlayback();
    }

    // Идёт заливка — отменяем запрос, файл на сервер не попадёт.
    if (track.status === 'uploading') {
      this.aborts[track.localId]?.abort();
      this.dropTrack(track);
      return;
    }

    // Уже залит — удаляем на сервере, показывая состояние.
    if (track.trackUploadId !== null && this.draftId !== null) {
      track.deleting = true;
      try {
        await AlbumUploadService.deleteTrackFile(this.draftId, track.trackUploadId);
      } catch (e) {
        track.deleting = false;
        this.errorMessage = apiErrorText(e, 'Не удалось удалить файл.');
        return;
      }
    }

    this.dropTrack(track);
  }

  private dropTrack(track: AlbumUploadFormTrack) {
    this.tracks = this.tracks.filter(t => t.localId !== track.localId);
  }

  public togglePlay(track: AlbumUploadFormTrack) {
    if (this.playingLocalId === track.localId) {
      this.stopPlayback();
      return;
    }

    this.stopPlayback();

    const url = URL.createObjectURL(track.file);
    const audio = new Audio(url);
    audio.addEventListener('ended', this.onPlaybackEnd);
    audio.addEventListener('error', this.onPlaybackEnd);
    audio.play().catch(() => this.onPlaybackEnd());

    this.audio = audio;
    this.playingUrl = url;
    this.playingLocalId = track.localId;
  }

  private onPlaybackEnd = (): void => {
    this.stopPlayback();
  };

  private stopPlayback() {
    if (this.audio) {
      this.audio.pause();
      this.audio.removeEventListener('ended', this.onPlaybackEnd);
      this.audio.removeEventListener('error', this.onPlaybackEnd);
      this.audio = null;
    }
    if (this.playingUrl) {
      URL.revokeObjectURL(this.playingUrl);
      this.playingUrl = '';
    }
    this.playingLocalId = null;
  }

  private validate(): string | null {
    if (!this.artistName.trim()) {
      return 'Укажите имя артиста.';
    }
    if (!this.albumName.trim()) {
      return 'Укажите название альбома.';
    }

    if (this.year.trim()) {
      const yearNum = Number(this.year);
      const currentYear = new Date().getFullYear();
      if (!Number.isInteger(yearNum) || yearNum < MIN_YEAR || yearNum > currentYear) {
        return `Год должен быть в диапазоне ${MIN_YEAR}–${currentYear}.`;
      }
    }

    if (this.tracks.length === 0) {
      return 'Добавьте хотя бы один трек.';
    }

    const seenIsrc = new Set<string>();
    for (const track of this.tracks) {
      if (track.deleting) {
        return `Файл «${track.originalFilename}» удаляется, дождитесь завершения.`;
      }
      if (track.status !== 'uploaded' || track.trackUploadId === null) {
        return `Файл «${track.originalFilename}» ещё не загрузился или загрузился с ошибкой.`;
      }
      if (!track.title.trim()) {
        return `Укажите название для «${track.originalFilename}».`;
      }
      const isrc = track.isrc.trim();
      if (isrc.length !== ISRC_LENGTH) {
        return `ISRC у «${track.originalFilename}» должен быть ровно ${ISRC_LENGTH} символов.`;
      }
      if (seenIsrc.has(isrc)) {
        return `ISRC ${isrc} указан у нескольких треков.`;
      }
      seenIsrc.add(isrc);
    }

    return null;
  }

  public async submit() {
    if (this.isCommitting) {
      return;
    }

    const validationError = this.validate();
    if (validationError) {
      this.errorMessage = validationError;
      return;
    }

    if (this.draftId === null) {
      this.errorMessage = 'Черновик ещё не создан.';
      return;
    }

    this.isCommitting = true;
    this.errorMessage = '';
    this.fails = {};

    try {
      await AlbumUploadService.commit(this.draftId, {
        albumName: this.albumName.trim(),
        artistName: this.artistName.trim(),
        year: this.year.trim() ? Number(this.year) : null,
        tracks: this.tracks.map(t => ({
          id: t.trackUploadId as number,
          title: t.title.trim(),
          isrc: t.isrc.trim()
        }))
      });

      // Сразу показать загрузку в доке, не дожидаясь редкого тика списка.
      albumUpload.context(this.$store).actions.refreshNow();

      // Переход на прогресс — легитимный, без предупреждения об уходе.
      this.committed = true;
      this.$router.push({
        name: 'albumUploadProgress',
        params: { id: String(this.draftId) }
      });
    } catch (e) {
      const err = e as APIError;
      this.errorMessage = err?.message || 'Ошибка';
      this.fails = err?.validationFails || {};
    } finally {
      this.isCommitting = false;
    }
  }
}
